// rollupEvaluator.js

// Utilities
import { computeChecklistProgress } from 'services/checklists/checklistModels';
import { formatPrice, parsePriceCents, resolveFieldCurrencyCode } from 'utilities/Currency';
import { normalizeMeasurementValueForStorage } from 'utilities/Measurement';
import { extractRichTextPlainText } from 'utilities/RichText';
import { getNextAnnualDateOccurrence } from 'utilities/AnnualDate';
import {
  getRollupAggregateOptions,
  getRollupFieldType,
  normalizeRollupDefinitions,
} from 'rollups/rollupDefinitions';

const numberFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 2,
});

const percentFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 1,
});

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

const normalizeString = (value) => String(value || '').trim();

const isFilledValue = (value) => {
  if (value == null) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'string') return normalizeString(value).length > 0;

  if (value && typeof value === 'object') {
    if (Object.prototype.hasOwnProperty.call(value, 'value') && Object.prototype.hasOwnProperty.call(value, 'unit')) {
      return Boolean(normalizeMeasurementValueForStorage(value));
    }

    if (Object.prototype.hasOwnProperty.call(value, 'startDate') || Object.prototype.hasOwnProperty.call(value, 'endDate')) {
      return Boolean(value.startDate || value.endDate);
    }
  }

  return true;
};

const toComparableDate = (value) => {
  if (value == null) return null;
  if (value instanceof Date) return value.getTime();
  if (value?.toDate && typeof value.toDate === 'function') return value.toDate().getTime();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
};

const toComparableTemporalPoint = (value, fieldType, referenceDate = new Date()) => {
  if (fieldType === 'annualdate') {
    return getNextAnnualDateOccurrence(value, referenceDate)?.getTime() ?? null;
  }

  return toComparableDate(value);
};

const toDateRange = (value) => {
  if (!value || typeof value !== 'object') return null;
  const start = toComparableDate(value.startDate || value.start || null);
  const end = toComparableDate(value.endDate || value.end || value.startDate || value.start || null);
  if (start == null || end == null) return null;
  return start <= end ? { start, end } : { start: end, end: start };
};

const extractTokenList = (value, fieldType) => {
  if (value == null) return [];
  if (fieldType === 'richtext') {
    const text = normalizeString(extractRichTextPlainText(value));
    return text ? [text] : [];
  }

  const list = Array.isArray(value) ? value : [value];

  return list
    .map((entry) => {
      if (typeof entry === 'string' || typeof entry === 'number') {
        return normalizeString(entry);
      }

      if (!entry || typeof entry !== 'object') return '';

      return normalizeString(
        entry.label
        || entry.title
        || entry.name
        || entry.value
        || entry.email
        || entry.userKey
        || entry.key
      );
    })
    .filter(Boolean);
};

const formatCompactList = (tokens) => {
  const list = Array.from(new Set((Array.isArray(tokens) ? tokens : []).filter(Boolean)));
  if (!list.length) return '--';
  if (list.length <= 3) return list.join(', ');
  return `${list.slice(0, 3).join(', ')} +${list.length - 3} more`;
};

const buildUnavailableResult = (rollup, reason, itemCount = 0) => ({
  ...rollup,
  itemCount,
  note: rollup?.description || reason,
  status: 'unavailable',
  valueDisplay: '--',
});

const buildValueResult = (rollup, valueDisplay, itemCount, fallbackNote = '') => ({
  ...rollup,
  itemCount,
  note: rollup?.description || fallbackNote,
  status: 'ready',
  valueDisplay,
});

const toNumericFieldValue = (rawValue, fieldType) => {
  if (fieldType === 'money') {
    const cents = parsePriceCents(rawValue);
    return Number.isFinite(cents) ? cents : null;
  }

  const numericValue = rawValue == null ? Number.NaN : Number(rawValue);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const formatNumberValue = (value, fieldType, suffix = '', currencyCode = 'USD') => {
  if (!Number.isFinite(value)) return '--';
  if (fieldType === 'percentage') return `${percentFormatter.format(value)}%`;
  if (fieldType === 'money') return formatPrice(value, currencyCode);
  const formattedNumber = numberFormatter.format(value);
  return `${formattedNumber}${suffix}`.trim();
};

const evaluateNumericRollup = ({ aggregate, fieldType, items, resolveRawValue, currencyCode = 'USD' }) => {
  if (fieldType === 'measurement') {
    const measurements = items
      .map((item) => normalizeMeasurementValueForStorage(resolveRawValue(item)))
      .filter(Boolean);

    if (!measurements.length) {
      if (aggregate === 'sum') return { displayValue: '0', note: '0 items' };
      return null;
    }

    const units = new Set(measurements.map((value) => normalizeString(value?.unit)));
    if (units.size > 1) {
      return {
        unavailableReason: 'Mixed measurement units',
      };
    }

    const unit = measurements[0]?.unit || '';
    const unitSuffix = unit ? ` ${unit}` : '';
    const values = measurements.map((value) => Number(value.value)).filter(Number.isFinite);
    if (!values.length) return null;

    if (aggregate === 'sum') return { displayValue: formatNumberValue(values.reduce((sum, value) => sum + value, 0), fieldType, unitSuffix, currencyCode) };
    if (aggregate === 'average') return { displayValue: formatNumberValue(values.reduce((sum, value) => sum + value, 0) / values.length, fieldType, unitSuffix, currencyCode) };
    if (aggregate === 'min') return { displayValue: formatNumberValue(Math.min(...values), fieldType, unitSuffix, currencyCode) };
    if (aggregate === 'max') return { displayValue: formatNumberValue(Math.max(...values), fieldType, unitSuffix, currencyCode) };
    if (aggregate === 'count_non_empty') return { displayValue: numberFormatter.format(values.length) };

    return null;
  }

  const values = items
    .map((item) => toNumericFieldValue(resolveRawValue(item), fieldType))
    .filter((value) => value != null);

  if (aggregate === 'count_non_empty') {
    return {
      displayValue: numberFormatter.format(values.length),
    };
  }

  if (!values.length) {
    if (aggregate === 'sum') {
      return {
        displayValue: fieldType === 'money' ? formatPrice(0, currencyCode) : '0',
      };
    }
    return null;
  }

  if (aggregate === 'sum') return { displayValue: formatNumberValue(values.reduce((sum, value) => sum + value, 0), fieldType, '', currencyCode) };
  if (aggregate === 'average') return { displayValue: formatNumberValue(values.reduce((sum, value) => sum + value, 0) / values.length, fieldType, '', currencyCode) };
  if (aggregate === 'min') return { displayValue: formatNumberValue(Math.min(...values), fieldType, '', currencyCode) };
  if (aggregate === 'max') return { displayValue: formatNumberValue(Math.max(...values), fieldType, '', currencyCode) };

  return null;
};

const evaluateBooleanRollup = ({ aggregate, items, resolveRawValue }) => {
  const values = items
    .map((item) => resolveRawValue(item))
    .filter((value) => typeof value === 'boolean');

  if (!values.length) {
    if (aggregate === 'count_true' || aggregate === 'count_false') {
      return { displayValue: '0' };
    }
    return null;
  }

  const trueCount = values.filter(Boolean).length;
  const falseCount = values.length - trueCount;

  if (aggregate === 'count_true') return { displayValue: numberFormatter.format(trueCount) };
  if (aggregate === 'count_false') return { displayValue: numberFormatter.format(falseCount) };
  if (aggregate === 'percent_true') return { displayValue: `${percentFormatter.format((trueCount / values.length) * 100)}%` };
  if (aggregate === 'percent_false') return { displayValue: `${percentFormatter.format((falseCount / values.length) * 100)}%` };
  if (aggregate === 'any_true') return { displayValue: trueCount > 0 ? 'Yes' : 'No' };
  if (aggregate === 'all_true') return { displayValue: trueCount === values.length ? 'Yes' : 'No' };

  return null;
};

const evaluateTemporalPointRollup = ({ aggregate, fieldType, items, resolveRawValue }) => {
  const referenceDate = new Date();
  const values = items
    .map((item) => toComparableTemporalPoint(resolveRawValue(item), fieldType, referenceDate))
    .filter((value) => value != null);

  if (aggregate === 'count_non_empty') {
    return {
      displayValue: numberFormatter.format(values.length),
    };
  }

  if (!values.length) return null;
  if (aggregate === 'earliest') return { displayValue: dateFormatter.format(new Date(Math.min(...values))) };
  if (aggregate === 'latest') return { displayValue: dateFormatter.format(new Date(Math.max(...values))) };
  return null;
};

const evaluateTemporalRangeRollup = ({ aggregate, items, resolveRawValue }) => {
  const ranges = items
    .map((item) => toDateRange(resolveRawValue(item)))
    .filter(Boolean);

  if (aggregate === 'count_non_empty') {
    return {
      displayValue: numberFormatter.format(ranges.length),
    };
  }

  if (!ranges.length) {
    if (aggregate === 'active_now_count') return { displayValue: '0' };
    return null;
  }

  const now = Date.now();

  if (aggregate === 'earliest_start') return { displayValue: dateFormatter.format(new Date(Math.min(...ranges.map((range) => range.start)))) };
  if (aggregate === 'latest_end') return { displayValue: dateFormatter.format(new Date(Math.max(...ranges.map((range) => range.end)))) };
  if (aggregate === 'active_now_count') return {
    displayValue: numberFormatter.format(ranges.filter((range) => range.start <= now && range.end >= now).length),
  };

  return null;
};

const buildTokenCounts = (items, resolveRawValue, fieldType) => {
  const tokenCounts = new Map();
  const orderedTokens = [];

  items.forEach((item) => {
    extractTokenList(resolveRawValue(item), fieldType).forEach((token) => {
      if (!tokenCounts.has(token)) {
        orderedTokens.push(token);
        tokenCounts.set(token, 0);
      }
      tokenCounts.set(token, (tokenCounts.get(token) || 0) + 1);
    });
  });

  return {
    orderedTokens,
    tokenCounts,
  };
};

const evaluateTokenRollup = ({ aggregate, fieldType, items, resolveRawValue }) => {
  if (aggregate === 'count_non_empty') {
    const filledCount = items.filter((item) => {
      const rawValue = resolveRawValue(item);
      return isFilledValue(rawValue);
    }).length;

    return {
      displayValue: numberFormatter.format(filledCount),
    };
  }

  const { orderedTokens, tokenCounts } = buildTokenCounts(items, resolveRawValue, fieldType);
  if (!orderedTokens.length) {
    if (aggregate === 'unique_count') return { displayValue: '0' };
    return null;
  }

  if (aggregate === 'unique_count') {
    return {
      displayValue: numberFormatter.format(orderedTokens.length),
    };
  }

  if (aggregate === 'show_list') {
    return {
      displayValue: formatCompactList(orderedTokens),
    };
  }

  if (aggregate === 'most_common' || aggregate === 'least_common') {
    const sortedEntries = Array.from(tokenCounts.entries()).sort((left, right) => {
      if (aggregate === 'most_common') {
        if (right[1] !== left[1]) return right[1] - left[1];
      } else if (left[1] !== right[1]) {
        return left[1] - right[1];
      }
      return left[0].localeCompare(right[0]);
    });

    return {
      displayValue: sortedEntries[0]?.[0] || '--',
    };
  }

  return null;
};

const evaluateChecklistRollup = ({ aggregate, items, resolveRawValue }) => {
  const progressList = items
    .map((item) => computeChecklistProgress(resolveRawValue(item)))
    .filter((progress) => Number(progress?.totalItems) > 0);

  if (!progressList.length) {
    if (
      aggregate === 'sum_total_items'
      || aggregate === 'sum_completed_items'
      || aggregate === 'count_fully_completed'
      || aggregate === 'count_started'
      || aggregate === 'count_not_started'
    ) {
      return { displayValue: '0' };
    }
    return null;
  }

  if (aggregate === 'sum_total_items') {
    return {
      displayValue: numberFormatter.format(progressList.reduce((sum, progress) => sum + Number(progress?.totalItems || 0), 0)),
    };
  }

  if (aggregate === 'sum_completed_items') {
    return {
      displayValue: numberFormatter.format(progressList.reduce((sum, progress) => sum + Number(progress?.completedItems || 0), 0)),
    };
  }

  if (aggregate === 'average_completion_percent') {
    const values = progressList.map((progress) => {
      const totalItems = Number(progress?.totalItems || 0);
      const completedItems = Number(progress?.completedItems || 0);
      return totalItems > 0 ? (completedItems / totalItems) * 100 : null;
    }).filter((value) => value != null);

    if (!values.length) return null;

    return {
      displayValue: `${percentFormatter.format(values.reduce((sum, value) => sum + value, 0) / values.length)}%`,
    };
  }

  if (aggregate === 'count_fully_completed') {
    return {
      displayValue: numberFormatter.format(progressList.filter((progress) => Number(progress?.completedItems || 0) >= Number(progress?.totalItems || 0)).length),
    };
  }

  if (aggregate === 'count_started') {
    return {
      displayValue: numberFormatter.format(progressList.filter((progress) => Number(progress?.completedItems || 0) > 0).length),
    };
  }

  if (aggregate === 'count_not_started') {
    return {
      displayValue: numberFormatter.format(progressList.filter((progress) => Number(progress?.completedItems || 0) <= 0).length),
    };
  }

  return null;
};

const evaluateSingleRollup = ({ fieldDef, items, rollup, resolveFieldValue }) => {
  const fieldType = getRollupFieldType(fieldDef);
  const aggregate = normalizeString(rollup?.aggregate).toLowerCase();
  const resolveRawValue = (item) => resolveFieldValue(item, fieldDef);

  if (fieldType === 'number' || fieldType === 'money' || fieldType === 'percentage' || fieldType === 'measurement') {
    return evaluateNumericRollup({
      aggregate,
      fieldType,
      items,
      resolveRawValue,
      currencyCode: resolveFieldCurrencyCode(fieldDef),
    });
  }

  if (fieldType === 'boolean') {
    return evaluateBooleanRollup({ aggregate, items, resolveRawValue });
  }

  if (fieldType === 'date' || fieldType === 'datetime' || fieldType === 'annualdate') {
    return evaluateTemporalPointRollup({ aggregate, fieldType, items, resolveRawValue });
  }

  if (fieldType === 'daterange' || fieldType === 'datetimerange') {
    return evaluateTemporalRangeRollup({ aggregate, items, resolveRawValue });
  }

  if (
    fieldType === 'enum'
    || fieldType === 'multiselect'
    || fieldType === 'relation'
    || fieldType === 'people'
    || fieldType === 'string'
    || fieldType === 'richtext'
    || fieldType === 'url'
    || fieldType === 'email'
    || fieldType === 'phone'
    || fieldType === 'barcode'
  ) {
    return evaluateTokenRollup({ aggregate, fieldType, items, resolveRawValue });
  }

  if (fieldType === 'checklist') {
    return evaluateChecklistRollup({ aggregate, items, resolveRawValue });
  }

  return null;
};

export function evaluateRollups({
  fields = [],
  items = [],
  rollups = [],
  resolveFieldValue,
}) {
  const normalizedRollups = normalizeRollupDefinitions(rollups);
  const fieldMap = new Map(
    (Array.isArray(fields) ? fields : []).map((field) => [normalizeString(field?.name || field?.key), field])
  );

  return normalizedRollups.map((rollup) => {
    const fieldKey = normalizeString(rollup?.fieldKey);
    const fieldDef = fieldMap.get(fieldKey) || null;
    const itemList = Array.isArray(items) ? items : [];

    if (!fieldDef) {
      return buildUnavailableResult(rollup, 'Field is missing', itemList.length);
    }

    const supportedAggregates = getRollupAggregateOptions(fieldDef);
    const isAggregateSupported = supportedAggregates.some((option) => option.value === rollup?.aggregate);
    if (!isAggregateSupported) {
      return buildUnavailableResult(rollup, 'Aggregate is not supported for this field', itemList.length);
    }

    const evaluation = evaluateSingleRollup({
      fieldDef,
      items: itemList,
      rollup,
      resolveFieldValue,
    });

    if (!evaluation) {
      return buildUnavailableResult(rollup, 'No data yet', itemList.length);
    }

    if (evaluation?.unavailableReason) {
      return buildUnavailableResult(rollup, evaluation.unavailableReason, itemList.length);
    }

    const fallbackNote = `${itemList.length} item${itemList.length === 1 ? '' : 's'}`;
    return buildValueResult(rollup, evaluation.displayValue || '--', itemList.length, fallbackNote);
  });
}
